import {ResearchLitElement, html, css, ifDefined} from '@core/research-lit-element';
import '@bog-design-system/bd-font';

export class ResearchProductPromoCard extends ResearchLitElement {
    static get is() {
        return 'research-product-promo-card';
    }

    static get styles() {
        // language=css
        return css`
            :host {
                display: flex;
                --card-width: 636px;
                --img-height: 310px;
                --img-width: 100%;
                --img-border-radius: var(--border-radius-30);
                --card-padding: var(--space-24);
                --card-border-radius: var(--border-radius-32);
                --text-color: var(--color-invert-component-tr-20);
                --card-border: solid 1px var(--color-invert-component-tr-60);
                --card-bg-color: var(--color-invert-background-solid-10);
                --card-bg-color-hover: var(--color-invert-background-solid-20);
                --card-bg-color-active: var(--color-invert-background-solid-30);
                --shadow-hover: var(--shadow-raised);
                --cursor: pointer;
                --img-margin-h: var(--space-24);
                --img-margin-v: var(--space-16);
                --container-align: var(--card-container-align, 'flex-start');
                --texts-align: var(--card-texts-align);
                --horizontal-description: var(--card-horizontal-description, none);
            }
            :host([static]) .container {
                cursor: default;
            }
            :host([horizontal]) {
                --img-height: 126px;
                --img-width: 168px;
            }
            :host([small]) {
                --card-width: 490px;
            }
            .container {
                flex-direction: column;
                transition: box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out;
            }
            :host(:not([horizontal])[reversed]) .container {
                flex-direction: column-reverse;
                justify-content: flex-end;
            }
            :host([horizontal]) .container {
                flex-direction: row;
                justify-content: space-between;
            }
            :host([horizontal][reversed]) .container {
                flex-direction: row-reverse;
                justify-content: flex-end;
            }
            .container {
                display: flex;
                flex-direction: column;
                padding: var(--card-padding);
                border-radius: var(--card-border-radius);
                cursor: pointer;
                box-sizing: border-box;
                text-decoration: none;
                flex-grow: 1;
                color: var(--text-color);
                border: var(--card-border);
                background-color: var(--card-bg-color);
                width: var(--card-width);
                align-items: var(--container-align);
            }
            :host(:not([static]):hover) .container {
                background-color: var(--card-bg-color-hover);
            }
            :host(:not([static]):active) .container {
                background-color: var(--card-bg-color-active);
            }
            :host(:not([static])[shadow]:hover) .container,
            :host(:not([static])[shadow]:active) .container {
                box-shadow: var(--shadow-hover);
            }
            .illustration {
                width: 100%;
                height: var(--img-height);
                min-width: var(--img-width);
                max-width: var(--img-width);
                background-position: center;
                background-size: cover;
                border-radius: var(--img-border-radius);
            }
            :host(:not([horizontal])) .illustration {
                margin-top: var(--img-margin-v);
            }
            :host(:not([horizontal])[reversed]) .illustration {
                margin-top: 0;
                margin-bottom: var(--img-margin-v);
            }
            :host([horizontal]) .illustration {
                margin-left: var(--img-margin-h);
                margin-right: 0;
            }
            :host([horizontal][reversed]) .illustration {
                margin-right: var(--img-margin-h);
                margin-left: 0;
            }
            .content-container {
                height: auto;
                display: flex;
                flex-direction: column;
                flex-grow: 1;
            }
            .badges-list ::slotted(*) {
                margin-bottom: var(--space-8);
            }
            ::slotted([slot='badge']) {
                margin-right: var(--space-12);
            }
            .texts-container {
                display: flex;
                flex-direction: column;
                align-items: var(--texts-align);
            }
            .texts-container .title {
                height: auto;
                display: block;
            }
            .texts-container .title ::slotted(*) {
                display: block;
                margin-bottom: var(--space-12);
            }
            .description ::slotted(*) {
                display: block;
                margin-bottom: var(--space-12);
            }

            @media (max-width: 1024px) {
                :host(:not([horizontal])) {
                    --card-width: 464px;
                    --img-height: 248px;
                }
                :host([horizontal]) {
                    --card-width: 100%;
                }
                :host([horizontal]) .container {
                    width: 100%;
                    max-width: 100%;
                }
            }

            @media (max-width: 600px) {
                :host {
                    --card-padding: var(--space-16);
                    --img-margin-h: var(--space-16);
                }
                :host([horizontal]) {
                    --img-height: 66px;
                    --img-width: 88px;
                }
                :host([horizontal]) .description {
                    display: var(--horizontal-description);
                }
                :host(:not([horizontal])) {
                    --img-height: 166px;
                }
            }
        `;
    }

    render() {
        // language=html
        return html`
            <a href="${ifDefined(this.href)}" target="${ifDefined(this.target)}" class="container">
                <div class="content-container">
                    <div class="badges-list">
                        <slot name="badge"></slot>
                    </div>

                    <div class="texts-container">
                        <div class="title">
                            <slot name="title"></slot>
                        </div>
                        <div class="description">
                            <slot name="description"></slot>
                        </div>
                    </div>

                    ${!this.hideLink ?
                        html`
                              <div class="link-button">
                                  <slot name="link-button"></slot>
                              </div>
                          ` :
                        ''}
                </div>

                ${!this.hideImg ?
                    html` <div class="illustration" style="background-image: url(${this.imgUrl})"></div> ` :
                    ''}
            </a>
        `;
    }

    static get properties() {
        return {
            href: {
                type: String,
            },
            target: {
                type: String,
            },
            imgUrl: {
                type: String,
            },
            small: {
                type: Boolean,
                reflect: true,
            },
            showDescription: {
                type: Boolean,
                reflect: true,
                attribute: 'show-description',
            },
            reversed: {
                type: Boolean,
                reflect: true,
            },
            horizontal: {
                type: Boolean,
                reflect: true,
            },
            static: {
                type: Boolean,
                reflect: true,
            },
            hideImg: {
                type: Boolean,
                reflect: true,
                attribute: 'hide-img',
            },
            hideLink: {
                type: Boolean,
                reflect: true,
                attribute: 'hide-link',
            },
            shadow: {
                type: Boolean,
                reflect: true,
                attribute: 'shadow',
            },
        };
    }
}

window.customElements.define(ResearchProductPromoCard.is, ResearchProductPromoCard);
